import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  ArrowLeft,
  Mail,
  Phone,
  User,
  Calendar,
  MessageSquare,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnquiriesManagement = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, new, contacted

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/enquiries`);
      if (response.data.success) {
        setEnquiries(response.data.enquiries);
      }
    } catch (error) {
      toast.error("Failed to fetch enquiries");
      console.error("Error fetching enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filteredEnquiries = enquiries.filter((enquiry) => {
    if (filter === "all") return true;
    return enquiry.status === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviation-blue mx-auto mb-4"></div>
          <p className="text-aviation-blue">Loading enquiries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm mb-8">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate("/operations")}
                className="border-aviation-blue text-aviation-blue"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-aviation-blue">
                Customer Enquiries
              </h1>
            </div>
            <Button
              onClick={fetchEnquiries}
              className="bg-aviation-gold hover:bg-aviation-gold-dark text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-l-4 border-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Enquiries</p>
                  <p className="text-3xl font-bold text-aviation-blue">
                    {enquiries.length}
                  </p>
                </div>
                <MessageSquare className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">New</p>
                  <p className="text-3xl font-bold text-aviation-blue">
                    {enquiries.filter((e) => e.status === "new").length}
                  </p>
                </div>
                <Mail className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-orange-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Today</p>
                  <p className="text-3xl font-bold text-aviation-blue">
                    {
                      enquiries.filter((e) => {
                        const today = new Date().toDateString();
                        const enquiryDate = new Date(
                          e.createdAt
                        ).toDateString();
                        return today === enquiryDate;
                      }).length
                    }
                  </p>
                </div>
                <Calendar className="w-10 h-10 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-3 mb-6">
          <Button
            variant={filter === "all" ? "default" : "outline"}
            onClick={() => setFilter("all")}
            className={
              filter === "all"
                ? "bg-aviation-blue text-white"
                : "border-aviation-blue text-aviation-blue"
            }
          >
            All ({enquiries.length})
          </Button>
          <Button
            variant={filter === "new" ? "default" : "outline"}
            onClick={() => setFilter("new")}
            className={
              filter === "new"
                ? "bg-green-500 text-white"
                : "border-green-500 text-green-500"
            }
          >
            New ({enquiries.filter((e) => e.status === "new").length})
          </Button>
        </div>

        {/* Enquiries List */}
        <div className="space-y-4">
          {filteredEnquiries.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No enquiries found</p>
                <p className="text-gray-400 text-sm mt-2">
                  Customer enquiries will appear here
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredEnquiries.map((enquiry) => (
              <Card
                key={enquiry.id}
                className={`hover:shadow-lg transition-all ${
                  enquiry.status === "new" ? "border-l-4 border-green-500" : ""
                }`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-aviation-blue/10 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-aviation-blue" />
                      </div>
                      <div>
                        <CardTitle className="text-aviation-blue text-lg">
                          {enquiry.name}
                        </CardTitle>
                        <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            <a
                              href={`mailto:${enquiry.email}`}
                              className="hover:text-aviation-blue hover:underline"
                            >
                              {enquiry.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            <a
                              href={`tel:${enquiry.phone}`}
                              className="hover:text-aviation-blue hover:underline"
                            >
                              {enquiry.phone}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full ${
                          enquiry.status === "new"
                            ? "bg-green-100 text-green-700"
                            : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {enquiry.status}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                        <Calendar className="w-3 h-3" />
                        {formatDate(enquiry.createdAt)}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Message:
                    </p>
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {enquiry.message}
                    </p>
                  </div>

                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={() => window.open(`mailto:${enquiry.email}`)}
                      variant="outline"
                      size="sm"
                      className="border-aviation-blue text-aviation-blue hover:bg-aviation-blue hover:text-white"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                    <Button
                      onClick={() => window.open(`tel:${enquiry.phone}`)}
                      variant="outline"
                      size="sm"
                      className="border-aviation-gold text-aviation-gold hover:bg-aviation-gold hover:text-white"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                    <Button
                      onClick={() =>
                        window.open(
                          `https://wa.me/91${enquiry.phone.replace(/\D/g, "")}`,
                          "_blank"
                        )
                      }
                      variant="outline"
                      size="sm"
                      className="border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiriesManagement;
